// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Link from "@insite/mobius/Link";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { color } from "@werner/XDL/Utilities";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    pageType = "pageType",
    overrideTitle = "overrideTitle",
    queryString = "queryString",
    testSelector = "testSelector",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.pageType]: string;
        [fields.overrideTitle]: string;
        [fields.queryString]: string;
        [fields.testSelector]: string;
    };
    // Xng-Change
    onClickHandler?: () => void;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    pageLink: getPageLinkByPageType(state, ownProps.fields.pageType),
});

// Xng-Change
// WES-1766
// add HasHistory

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasHistory;

export interface PageTypeLinkStyles {
    clickable?: ClickablePresentationProps;
}

export const pageTypeLinkStyles: PageTypeLinkStyles = {
    clickable: {
        css: css`
            color: ${color("text.link")};
        `,
    },
};

const styles = pageTypeLinkStyles;

export const PageTypeLink: React.FC<Props> = ({
    pageLink,
    fields: {
        queryString,
        testSelector,
        overrideTitle,
    },
    onClickHandler,
    history,
}) => {
    if (!pageLink) {
        return null;
    }

    const url = queryString ? `${pageLink.url}?${queryString}` : pageLink.url;

    const selector = testSelector;

    /**
     * Xng-Change
     * WES-1766
     * Check if onClickHandler is parsed from props or not
     * if onClickHandler is populated render the component as a button with onClick prop
     * if not render it as <a> the OOTB behavior
     * in this case the anchor tag <a> will not have the both href and onClick props
     */
    const handleClick = () => {
        history.push(url);
        onClickHandler?.();
    };

    const LinkComponent = onClickHandler ? Clickable : Link;
    const linkProps = onClickHandler ? { onClick: handleClick } : { href: url };

    return (
        <LinkComponent data-test-selector={selector} {...linkProps} {...styles.clickable}>
            {overrideTitle || pageLink.title}
        </LinkComponent>
    );
};

const pageTypeLink: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(PageTypeLink)),
    definition: {
        group: "Basic",
        icon: "link",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.pageType,
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
            },
            {
                fieldType: "Translatable",
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.queryString,
                editorTemplate: "TextField",
                defaultValue: "",
            },
        ],
    },
};

export default pageTypeLink;
